import React, { useEffect, useState } from "react";
import { X, Plus } from "lucide-react";
import { toast } from "sonner";
import { createGap, listSalonServices } from "../../lib/api";
import { melDate, melLocalInput } from "../../lib/datetime";

const CATEGORIES = ["Hair", "Skin", "Brows + Lashes", "Body", "Nails", "Sauna", "Contrast Therapy", "Bathhouse", "Float Tank", "Infrared Sauna", "Massage"];
const inputClass = "w-full border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso text-[15px] bg-bone";

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="block text-xs text-ink/60 mb-1.5">{label}</span>
      {children}
      {hint && <span className="block text-[10px] text-ink/40 mt-1">{hint}</span>}
    </label>
  );
}

const blank = (suburb) => ({ service: "", category: "Hair", suburb: suburb || "", date: melDate(0), start: "", duration_min: 60, original_price: "", offer_price: "", promote_social: false });

export default function AddGapDialog({ open, onClose, onCreated, salonSlug, defaultSuburb }) {
  const [form, setForm] = useState(blank(defaultSuburb));
  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(false);
  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    if (!open) return;
    setForm(blank(defaultSuburb));
    listSalonServices().then((items) => setMenu(items || [])).catch(() => setMenu([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open && defaultSuburb) setForm((prev) => (prev.suburb ? prev : { ...prev, suburb: defaultSuburb }));
  }, [open, defaultSuburb]);

  if (!open) return null;
  const maxDate = melDate(30);
  const original = Number(form.original_price);
  const offer = Number(form.offer_price);
  const discount = original > 0 && offer > 0 && offer < original ? Math.round((1 - offer / original) * 100) : null;

  const pickMenu = (id) => {
    const svc = menu.find((s) => s.id === id);
    if (!svc) return;
    setForm((prev) => ({ ...prev, service: svc.name, category: svc.category, duration_min: svc.duration_min, original_price: String(svc.standard_price), offer_price: String(Math.max(5, Math.round(svc.standard_price * 0.7))) }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!form.date || !form.start || `${form.date}T${form.start}` <= melLocalInput()) { toast.error("Choose a start time in the future (Melbourne time)."); return; }
    if (offer >= original) { toast.error("Deal price must be lower than the usual price."); return; }
    if (offer < 5) { toast.error("Minimum deal price is $5."); return; }
    try {
      setLoading(true);
      const gap = await createGap({
        salon_slug: salonSlug, service: form.service.trim(), category: form.category, suburb: form.suburb.trim(),
        starts_at_local: `${form.date}T${form.start}`, duration_min: Number(form.duration_min) || 60,
        original_price: original, offer_price: offer, promote_social: form.promote_social,
      });
      toast.success(`Live now at the top of the deals feed · ${gap.time}`);
      onCreated && onCreated(gap);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not post the opening.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-espresso/30 backdrop-blur-sm sm:px-4" onClick={onClose} data-testid="add-gap-dialog">
      <div className="relative bg-bone w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-xl p-6 sm:p-8 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-sand/60 flex items-center justify-center transition-colors" data-testid="add-gap-close" aria-label="Close"><X size={16} strokeWidth={1.5} /></button>
        <p className="text-[11px] tracking-[0.22em] uppercase text-mocha">New opening</p>
        <h2 className="mt-2 font-serif text-2xl md:text-3xl text-ink leading-tight">Post a last-minute opening.</h2>
        <p className="mt-1 text-sm text-ink/55">It goes live instantly at the top of the deals feed and can be booked and paid for straight away.</p>
        <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="add-gap-form">
          {menu.length > 0 && (
            <Field label="Load from your menu">
              <select defaultValue="" onChange={(e) => pickMenu(e.target.value)} className={inputClass} data-testid="add-gap-menu"><option value="">Pick a saved service…</option>{menu.map((s) => <option key={s.id} value={s.id}>{s.name} · ${s.standard_price}</option>)}</select>
            </Field>
          )}
          <Field label="Service"><input type="text" required value={form.service} onChange={(e) => set("service", e.target.value)} placeholder="e.g. Balayage refresh" className={inputClass} data-testid="add-gap-service" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Service type"><select value={form.category} onChange={(e) => set("category", e.target.value)} className={inputClass} data-testid="add-gap-category">{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></Field>
            <Field label="Suburb"><input type="text" required value={form.suburb} onChange={(e) => set("suburb", e.target.value)} placeholder="Fitzroy" className={inputClass} data-testid="add-gap-suburb" /></Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Date"><input type="date" required min={melDate(0)} max={maxDate} value={form.date} onChange={(e) => set("date", e.target.value)} className={inputClass} data-testid="add-gap-date" /></Field>
            <Field label="Start time"><input type="time" required step="900" value={form.start} onChange={(e) => set("start", e.target.value)} className={inputClass} data-testid="add-gap-time" /></Field>
            <Field label="Minutes"><input type="number" min="15" step="15" value={form.duration_min} onChange={(e) => set("duration_min", e.target.value)} className={inputClass} data-testid="add-gap-duration" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Usual price $"><input type="number" required min="6" value={form.original_price} onChange={(e) => set("original_price", e.target.value)} placeholder="120" className={inputClass} data-testid="add-gap-original" /></Field>
            <Field label="Deal price $" hint={discount ? `${discount}% off · $5 minimum` : "$5 minimum"}><input type="number" required min="5" value={form.offer_price} onChange={(e) => set("offer_price", e.target.value)} placeholder="75" className={inputClass} data-testid="add-gap-offer" /></Field>
          </div>
          <label className="flex items-start gap-3 text-sm text-ink/70"><input type="checkbox" checked={form.promote_social} onChange={(e) => set("promote_social", e.target.checked)} className="mt-0.5 h-4 w-4 accent-espresso" data-testid="add-gap-promote-toggle" /><span>Also queue an Instagram promotion for this opening.</span></label>
          <button type="submit" disabled={loading} className={`w-full inline-flex items-center justify-center gap-2 rounded-full bg-espresso text-bone py-3 text-sm transition-colors ${loading ? "opacity-60" : "hover:bg-ink"}`} data-testid="add-gap-submit"><Plus size={14} strokeWidth={1.75} /> {loading ? "Posting…" : "Post opening"}</button>
        </form>
      </div>
    </div>
  );
}
